<template>
    <div class="projet-gsb">
        <div class="back-button" @click="$router.push('/projets')">
            <i class="fas fa-arrow-left"></i> Retour
        </div>
        <div class="projet-header">
            <div class="header-content">
                <img src="@/assets/gsblogo.png" alt="Logo GSB" class="projet-logo">
                <div class="header-text">
                    <h1>Projet GSB</h1>
                    <div class="projet-meta">
                        <span class="date">Septembre 2025 — Juin 2026</span>
                        <span class="category educational">Projet Scolaire — 2ème année BTS SIO SISR</span>
                    </div>
                    <div class="competences-badges">
                        <span class="competence-badge">Gérer le patrimoine informatique</span>
                        <span class="competence-badge">Répondre aux incidents et aux demandes d'assistance et d'évolution</span>
                        <span class="competence-badge">Développer la présence en ligne de l'organisation</span>
                        <span class="competence-badge">Travailler en mode projet</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="projet-content">

            <section class="projet-section">
                <h2>Présentation du projet</h2>
                <p>
                    Le projet GSB est un projet d'Atelier Professionnel réalisé tout au long de la deuxième
                    année de BTS SIO option SISR. L'objectif était de concevoir et déployer l'infrastructure
                    réseau complète d'une entreprise fictive <strong>Galaxy Swiss Bourdin</strong>, en mettant
                    l'accent sur la <strong>sécurité en profondeur</strong>, la <strong>haute disponibilité</strong>
                    et la <strong>répartition de charge</strong>.
                </p>
                <p>
                    L'infrastructure repose sur une architecture <strong>multi-site</strong> interconnectée par
                    VPN, entièrement virtualisée sous <strong>VMware vSphere</strong>, avec une segmentation réseau
                    stricte par VLANs, une DMZ pour les services exposés et des mécanismes de redondance sur les
                    services critiques.
                </p>
            </section>

            <section class="projet-section">
                <h2>Infrastructure déployée</h2>
                <div class="infrastructure-grid">
                    <div class="infra-item">
                        <h3><i class="fas fa-shield-halved"></i> Sécurité périmétrique</h3>
                        <ul>
                            <li><strong>OPNsense</strong> : pare-feu central gérant le trafic entre les VLANs, la DMZ et internet</li>
                            <li><strong>DMZ</strong> : zone démilitarisée isolant les services exposés du LAN interne</li>
                            <li><strong>VPN site-à-site</strong> : tunnel chiffré reliant le site principal au site distant</li>
                            <li><strong>Règles de filtrage</strong> inter-VLANs selon le principe du moindre privilège</li>
                        </ul>
                    </div>
                    <div class="infra-item">
                        <h3><i class="fas fa-globe"></i> DMZ & Services web</h3>
                        <ul>
                            <li><strong>Proxy</strong> : filtrage et contrôle des accès sortants des utilisateurs</li>
                            <li><strong>Reverse Proxy</strong> : point d'entrée unique pour les requêtes HTTP/HTTPS entrantes</li>
                            <li><strong>Load Balancer</strong> : répartition de charge sur les serveurs web pour la haute disponibilité</li>
                            <li><strong>Apache2</strong> : serveurs web hébergeant les applications de l'entreprise</li>
                        </ul>
                    </div>
                    <div class="infra-item">
                        <h3><i class="fas fa-network-wired"></i> DNS & DHCP redondants</h3>
                        <ul>
                            <li><strong>NS1 (VLAN ONE)</strong> et <strong>NS2 (VLAN TWO)</strong> : deux serveurs DNS sur des VLANs distincts</li>
                            <li>Résolution de noms garantie même en cas de panne d'un serveur</li>
                            <li><strong>DHCP redondant</strong> : attribution d'adresses IP sans interruption de service</li>
                            <li><strong>IPAM</strong> : gestion centralisée du plan d'adressage</li>
                        </ul>
                    </div>
                    <div class="infra-item">
                        <h3><i class="fas fa-database"></i> Bases de données & Messagerie</h3>
                        <ul>
                            <li><strong>DB1 / DB2</strong> : deux serveurs de bases de données en réplication pour la redondance</li>
                            <li><strong>SMTPOUT</strong> : serveur de messagerie sortante</li>
                            <li><strong>Zimbra</strong> : solution de messagerie et collaboration interne</li>
                        </ul>
                    </div>
                    <div class="infra-item">
                        <h3><i class="fas fa-hard-drive"></i> Sauvegardes</h3>
                        <ul>
                            <li><strong>Backup Service</strong> : service dédié à la gestion des sauvegardes de l'entreprise</li>
                            <li>Sauvegarde des VMs, bases de données et données utilisateurs</li>
                            <li>Politique de rétention et de restauration définie</li>
                        </ul>
                    </div>
                    <div class="infra-item">
                        <h3><i class="fas fa-users"></i> Segmentation utilisateurs</h3>
                        <ul>
                            <li><strong>VLAN Admin SYS</strong> : postes d'administration système, accès étendu</li>
                            <li><strong>VLAN Direction</strong> : postes de direction, accès restreint aux ressources sensibles</li>
                            <li><strong>VLAN Commercial</strong> : postes commerciaux, accès limité aux outils métier</li>
                            <li><strong>Active Directory</strong> : gestion centralisée des utilisateurs et GPO</li>
                        </ul>
                    </div>
                    <div class="infra-item">
                        <h3><i class="fas fa-building"></i> Site distant (VPN)</h3>
                        <ul>
                            <li>Réseau <strong>172.17.2.0/16</strong> relié au site principal par VPN site-à-site</li>
                            <li><strong>VLAN DEV</strong> : postes de développement isolés</li>
                            <li><strong>VLAN Workstations</strong> : postes physiques et postes dématérialisés</li>
                            <li>Accès aux ressources internes via le tunnel VPN chiffré</li>
                        </ul>
                    </div>
                    <div class="infra-item">
                        <h3><i class="fas fa-server"></i> Virtualisation</h3>
                        <ul>
                            <li><strong>VMware vSphere</strong> : plateforme de virtualisation hébergeant l'ensemble des VMs</li>
                            <li>Gestion des ressources, snapshots et migration à chaud</li>
                            <li>Haute disponibilité au niveau hyperviseur</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Schéma d'architecture réseau</h2>
                <div class="schema-container">
                    <img src="@/assets/diagrammeGSB.png" alt="Schéma d'architecture réseau du projet GSB" class="schema-image">
                </div>
            </section>

            <section class="projet-section">
                <h2>Défense en profondeur</h2>
                <p>
                    L'architecture a été conçue selon le principe de <strong>défense en profondeur</strong> :
                    plusieurs couches de sécurité indépendantes sont empilées de sorte qu'une compromission
                    d'un élément ne mette pas en danger l'ensemble du système.
                </p>
                <div class="defense-layers">
                    <div class="layer">
                        <div class="layer-number">1</div>
                        <div class="layer-content">
                            <h3>Périmètre</h3>
                            <p>Pare-feu OPNsense filtrant tout le trafic entrant et sortant depuis internet. Seuls les ports nécessaires sont ouverts.</p>
                        </div>
                    </div>
                    <div class="layer-arrow"><i class="fas fa-arrow-down"></i></div>
                    <div class="layer">
                        <div class="layer-number">2</div>
                        <div class="layer-content">
                            <h3>DMZ</h3>
                            <p>Zone tampon hébergeant les services exposés (proxy, reverse proxy). Aucune connexion directe depuis internet vers le LAN.</p>
                        </div>
                    </div>
                    <div class="layer-arrow"><i class="fas fa-arrow-down"></i></div>
                    <div class="layer">
                        <div class="layer-number">3</div>
                        <div class="layer-content">
                            <h3>Segmentation VLAN</h3>
                            <p>Chaque département est isolé dans son propre VLAN. Les flux inter-VLANs passent par le pare-feu avec des règles strictes.</p>
                        </div>
                    </div>
                    <div class="layer-arrow"><i class="fas fa-arrow-down"></i></div>
                    <div class="layer">
                        <div class="layer-number">4</div>
                        <div class="layer-content">
                            <h3>Contrôle d'accès</h3>
                            <p>Active Directory avec GPO imposant les politiques de sécurité sur les postes et contrôlant l'accès aux ressources par profil utilisateur.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Haute disponibilité & Répartition de charge</h2>
                <div class="ha-grid">
                    <div class="ha-item">
                        <div class="ha-icon"><i class="fas fa-rotate"></i></div>
                        <h3>DNS / DHCP redondants</h3>
                        <p>Deux serveurs DNS déployés sur des VLANs distincts (ONE et TWO). En cas de panne de l'un, l'autre prend automatiquement le relais.</p>
                    </div>
                    <div class="ha-item">
                        <div class="ha-icon"><i class="fas fa-scale-balanced"></i></div>
                        <h3>Load Balancing web</h3>
                        <p>Un reverse proxy répartit les requêtes HTTP/HTTPS entre plusieurs serveurs web Apache2, évitant tout point de défaillance unique.</p>
                    </div>
                    <div class="ha-item">
                        <div class="ha-icon"><i class="fas fa-database"></i></div>
                        <h3>Réplication base de données</h3>
                        <p>DB1 et DB2 fonctionnent en réplication : les données sont synchronisées en temps réel entre les deux instances pour garantir la continuité.</p>
                    </div>
                    <div class="ha-item">
                        <div class="ha-icon"><i class="fas fa-hard-drive"></i></div>
                        <h3>Plan de sauvegarde</h3>
                        <p>Service de sauvegarde dédié couvrant les VMs, les bases de données et les données utilisateurs, avec politique de rétention et procédures de restauration.</p>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Compétences développées</h2>
                <div class="skills-grid">
                    <div class="skill-item">
                        <h3>Virtualisation</h3>
                        <p>Déploiement et administration d'une infrastructure complète sous VMware vSphere</p>
                    </div>
                    <div class="skill-item">
                        <h3>Sécurité réseau</h3>
                        <p>Configuration d'OPNsense, règles de filtrage, DMZ, VPN site-à-site et défense en profondeur</p>
                    </div>
                    <div class="skill-item">
                        <h3>Haute disponibilité</h3>
                        <p>Mise en place de la redondance DNS/DHCP, réplication de bases de données et load balancing</p>
                    </div>
                    <div class="skill-item">
                        <h3>Administration Windows</h3>
                        <p>Configuration d'Active Directory, gestion des GPO et des profils utilisateurs</p>
                    </div>
                    <div class="skill-item">
                        <h3>Architecture réseau</h3>
                        <p>Conception d'un plan d'adressage multi-site, segmentation VLAN et routage inter-VLAN</p>
                    </div>
                    <div class="skill-item">
                        <h3>Gestion de projet</h3>
                        <p>Planification, documentation et déploiement d'une infrastructure sur une année complète</p>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Conclusion</h2>
                <p>
                    Le projet GSB a été l'occasion de concevoir et déployer une infrastructure d'entreprise
                    complète, du pare-feu périmétrique aux postes utilisateurs, en passant par les services
                    internes et la liaison inter-sites. La mise en œuvre de la défense en profondeur, de la
                    haute disponibilité et de la répartition de charge a demandé une vision globale de
                    l'architecture et une compréhension fine des interdépendances entre les services.
                </p>
                <p>
                    Ce projet, mené tout au long de l'année, m'a permis de consolider l'ensemble des
                    compétences du BTS SIO SISR et de me confronter à des problématiques réelles
                    d'administration système et réseau en entreprise.
                </p>
            </section>

        </div>
    </div>
</template>

<script>
export default {
    name: 'ProjetGSB'
}
</script>

<style scoped>
.projet-gsb {
    padding: 2rem;
    min-height: 100vh;
    max-width: 1200px;
    margin: 0 auto;
    color: #e0e0e0;
}

.back-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #2196F3;
    font-size: 1.1rem;
    margin-bottom: 2rem;
    cursor: pointer;
    transition: color 0.3s;
}

.back-button:hover {
    color: #64B5F6;
}

.projet-header {
    margin-bottom: 3rem;
    border-bottom: 2px solid #2196F3;
    padding-bottom: 1.5rem;
}

.header-content {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.projet-logo {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #2196F3;
    flex-shrink: 0;
}

.header-text {
    flex: 1;
}

h1 {
    color: #2196F3;
    font-size: 2.8rem;
    margin-bottom: 1rem;
}

.projet-meta {
    display: flex;
    gap: 1.5rem;
    font-size: 1.1rem;
    flex-wrap: wrap;
    align-items: center;
}

.date {
    color: #64B5F6;
}

.category {
    padding: 0.3rem 1rem;
    border-radius: 20px;
    font-weight: 500;
}

.category.educational {
    background-color: rgba(33, 150, 243, 0.1);
    color: #2196F3;
    border: 1px solid #2196F3;
}

.projet-section {
    margin-bottom: 3rem;
}

h2 {
    color: #64B5F6;
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    border-left: 4px solid #2196F3;
    padding-left: 1rem;
}

h3 {
    color: #90CAF9;
    font-size: 1.2rem;
    margin-bottom: 0.8rem;
}

p {
    line-height: 1.7;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    color: #B0BEC5;
}

strong {
    color: #e0e0e0;
}

/* Infrastructure grid */
.infrastructure-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
}

.infra-item {
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #2196F3;
    border-radius: 8px;
    padding: 1.5rem;
}

.infra-item h3 {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 1rem;
}

.infra-item h3 i {
    color: #2196F3;
}

ul {
    padding-left: 1.2rem;
    margin: 0;
}

li {
    margin-bottom: 0.6rem;
    line-height: 1.5;
    color: #B0BEC5;
}

/* Schema */
.schema-container {
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #2196F3;
    border-radius: 8px;
    padding: 1.5rem;
    text-align: center;
}

.schema-image {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
}

/* Defense layers */
.defense-layers {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin: 2rem 0;
    max-width: 700px;
}

.layer {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #2196F3;
    border-radius: 8px;
    padding: 1.2rem 1.5rem;
}

.layer-number {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #2196F3;
    color: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1rem;
    flex-shrink: 0;
}

.layer-content h3 {
    margin-bottom: 0.3rem;
    font-size: 1.1rem;
}

.layer-content p {
    margin: 0;
    font-size: 1rem;
}

.layer-arrow {
    display: flex;
    justify-content: center;
    color: #2196F3;
    font-size: 1.1rem;
    padding: 0.4rem 0;
}

/* HA grid */
.ha-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
}

.ha-item {
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #2196F3;
    border-radius: 8px;
    padding: 1.5rem;
    text-align: center;
}

.ha-icon {
    font-size: 2rem;
    color: #2196F3;
    margin-bottom: 1rem;
}

.ha-item h3 {
    text-align: center;
    margin-bottom: 0.8rem;
}

.ha-item p {
    margin: 0;
    font-size: 1rem;
    text-align: left;
}

/* Skills */
.skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
}

.skill-item {
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #2196F3;
    border-radius: 8px;
    padding: 1.5rem;
}

.skill-item p {
    margin: 0;
    font-size: 1rem;
}

.competences-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1rem;
}

.competence-badge {
    font-size: 0.82rem;
    font-style: italic;
    padding: 0.2rem 0.7rem;
    border-radius: 4px;
    border-left: 2px solid rgba(33, 150, 243, 0.5);
    color: #90CAF9;
    background: rgba(33, 150, 243, 0.1);
}

@media screen and (max-width: 768px) {
    .header-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .projet-logo {
        width: 60px;
        height: 60px;
    }

    h1 {
        font-size: 2rem;
    }

    .infrastructure-grid,
    .ha-grid,
    .skills-grid {
        grid-template-columns: 1fr;
    }

    .defense-layers {
        max-width: 100%;
    }
}
</style>
